import { z } from 'zod';

/**
 * Extensible plan × feature access matrix (PRE-121 / PRE-247).
 *
 * - `PLAN_IDS`: commercial tiers. Add a plan here when RC gets a new entitlement.
 * - `FEATURE_IDS`: gated capabilities. Add a feature, set defaults per plan, then
 *   enforce at the API boundary (and optionally gate the mobile UI).
 * - Limits are either a non-negative daily/usage ceiling or `'unlimited'`.
 *   `0` means "limit disabled" (same semantics as legacy freeTier knobs).
 */

export const PLAN_IDS = ['free', 'pro'] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const planIdSchema = z.enum(PLAN_IDS);

export const FEATURE_IDS = ['daily_sessions', 'daily_hints'] as const;
export type FeatureId = (typeof FEATURE_IDS)[number];
export const featureIdSchema = z.enum(FEATURE_IDS);

export const featureLimitSchema = z.union([
  z.number().int().min(0).max(100_000),
  z.literal('unlimited'),
]);
export type FeatureLimit = z.infer<typeof featureLimitSchema>;

export const planFeatureMapSchema = z
  .object({
    daily_sessions: featureLimitSchema,
    daily_hints: featureLimitSchema,
  })
  .strict();
export type PlanFeatureMap = z.infer<typeof planFeatureMapSchema>;

/** Sparse admin override: any plan/feature may be omitted → fall back to defaults. */
export const planFeaturesOverrideSchema = z
  .object({
    free: planFeatureMapSchema.partial().optional(),
    pro: planFeatureMapSchema.partial().optional(),
  })
  .strict();
export type PlanFeaturesOverride = z.infer<typeof planFeaturesOverrideSchema>;

/** Compiled-in defaults — free matches legacy BR-36; pro removes daily caps. */
export const DEFAULT_PLAN_FEATURES: Record<PlanId, PlanFeatureMap> = {
  free: { daily_sessions: 5, daily_hints: 15 },
  pro: { daily_sessions: 'unlimited', daily_hints: 'unlimited' },
};

/**
 * RevenueCat entitlement identifiers that unlock plan `pro`.
 * RC project currently ships lookup_key `Prepzio Pro`; also accept `pro` / `premium`.
 */
export const PRO_ENTITLEMENT_IDS = ['pro', 'Prepzio Pro', 'premium'] as const;

export function planFromEntitlementIds(entitlementIds: readonly string[]): PlanId {
  const set = new Set(entitlementIds);
  for (const id of PRO_ENTITLEMENT_IDS) {
    if (set.has(id)) return 'pro';
  }
  return 'free';
}

export function isUnlimitedLimit(limit: FeatureLimit): boolean {
  return limit === 'unlimited' || limit === 0;
}

/**
 * Resolve the effective limit for a plan × feature.
 * Precedence: admin `billing.planFeatures` override → (free only) legacy freeTier
 * knobs when provided → compiled default.
 */
export function resolveFeatureLimit(
  plan: PlanId,
  feature: FeatureId,
  overrides?: PlanFeaturesOverride | null,
  legacyFreeTier?: { dailySessions?: number; dailyHints?: number } | null,
): FeatureLimit {
  const fromOverride = overrides?.[plan]?.[feature];
  if (fromOverride !== undefined) return fromOverride;

  if (plan === 'free' && legacyFreeTier) {
    if (feature === 'daily_sessions' && legacyFreeTier.dailySessions !== undefined) {
      return legacyFreeTier.dailySessions;
    }
    if (feature === 'daily_hints' && legacyFreeTier.dailyHints !== undefined) {
      return legacyFreeTier.dailyHints;
    }
  }

  return DEFAULT_PLAN_FEATURES[plan][feature];
}

/** Numeric ceiling for counters; `null` means do not enforce. */
export function featureLimitToCeiling(limit: FeatureLimit): number | null {
  if (limit === 'unlimited' || limit === 0) return null;
  return limit;
}
